/**
 * Maps scheduling selections (session date/time, presentation date/time)
 * into CartItemAppointmentCustomizationDto objects for persisting to the cart API.
 */
import { PromptType } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'
import type {
  AppointmentPromptDto,
  CartItemAppointmentCustomizationDto,
  CartItemStorefrontDto,
  PromptDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

// =============================================
// Types
// =============================================

export interface SchedulingCustomizationParams {
  /** Cart items on the session bundle. */
  sessionCartItems: CartItemStorefrontDto[]
  /** CADY location ID for the session. */
  locationId: number
  /** Display name of the location (e.g. "Northbrook Studio"). */
  locationName: string
  /** Full formatted address of the location (e.g. "123 Main St, Springfield, IL 62701"). */
  locationAddress: string
  /** CADY session type ID. */
  sessionTypeId: number
  /** ISO date string for the session. */
  sessionDate: string
  /** Time string for the session (e.g. "2:00 PM"). */
  sessionTime: string
  /** Surcharge tier for the session date (e.g. "Tier 1"), or null. */
  sessionSurchargeTier: string | null
  /** Duration of the session in minutes. */
  sessionDuration: number
  /** Server-issued signature for the session slot. */
  sessionSig: string
  /** ISO date string for the presentation. Null when not applicable. */
  presentationDate: string | null
  /** Time string for the presentation. Null when not applicable. */
  presentationTime: string | null
  /** Surcharge tier for the presentation date, or null. */
  presentationSurchargeTier: string | null
  /** Duration of the presentation in minutes. Null when not applicable. */
  presentationDuration: number | null
  /** Server-issued signature for the presentation slot. Null when not applicable. */
  presentationSig: string | null
  /** CADY session type ID for the presentation slot. Null when no presentation. */
  presentationSessionTypeId: number | null
}

// =============================================
// Helpers
// =============================================

/**
 * Find the appointment prompt for a session or presentation slot.
 * Matches on `AppointmentPromptDto.isPresentation` rather than promptName.
 * Returns the cartItemId of the owning cart item alongside the prompt.
 */
function findAppointmentPrompt(
  cartItems: CartItemStorefrontDto[],
  isPresentation: boolean
): { cartItemId: string; prompt: PromptDto } | null {
  for (const cartItem of cartItems) {
    const prompt = (cartItem.item?.prompts ?? []).find(
      (p) =>
        p.promptType === PromptType.AppointmentPrompt &&
        (p as AppointmentPromptDto).isPresentation === isPresentation
    )
    if (prompt) return { cartItemId: cartItem.cartItemId, prompt }
  }
  return null
}

/**
 * Build a single CartItemAppointmentCustomizationDto for a scheduling slot.
 */
function buildAppointmentCustomization(
  cartItemId: string,
  prompt: PromptDto,
  locationId: number,
  locationName: string,
  locationAddress: string,
  sessionTypeId: number,
  date: string,
  time: string,
  surchargeTier: string | null,
  duration: number,
  sig: string
): CartItemAppointmentCustomizationDto {
  return {
    customizationType: 'CartItemAppointmentCustomizationDto',
    cartItemId,
    promptId: prompt.promptId,
    promptName: prompt.promptName,
    locationId,
    locationName,
    locationAddress,
    sessionTypeId,
    date,
    time,
    surchargeTier: surchargeTier ?? '',
    duration,
    sig
  }
}

// =============================================
// Public API
// =============================================

/**
 * Convert scheduling selections into CartItemAppointmentCustomizationDto[] for the cart API.
 *
 * Returns an empty array if no session appointment prompt can be found on the
 * session cart item. Presentation is only included when all presentation fields
 * are provided and the presentation prompt exists on the cart item.
 */
export function buildSchedulingCustomizations(
  params: SchedulingCustomizationParams
): CartItemAppointmentCustomizationDto[] {
  const result: CartItemAppointmentCustomizationDto[] = []
  const sessionMatch = findAppointmentPrompt(params.sessionCartItems, false)
  if (sessionMatch) {
    result.push(
      buildAppointmentCustomization(
        sessionMatch.cartItemId,
        sessionMatch.prompt,
        params.locationId,
        params.locationName,
        params.locationAddress,
        params.sessionTypeId,
        params.sessionDate,
        params.sessionTime,
        params.sessionSurchargeTier,
        params.sessionDuration,
        params.sessionSig
      )
    )
  }
  if (
    params.presentationDate !== null &&
    params.presentationTime !== null &&
    params.presentationDuration !== null &&
    params.presentationSig !== null &&
    params.presentationSessionTypeId !== null
  ) {
    const presentationMatch = findAppointmentPrompt(params.sessionCartItems, true)
    if (presentationMatch) {
      result.push(
        buildAppointmentCustomization(
          presentationMatch.cartItemId,
          presentationMatch.prompt,
          params.locationId,
          params.locationName,
          params.locationAddress,
          params.presentationSessionTypeId,
          params.presentationDate,
          params.presentationTime,
          params.presentationSurchargeTier,
          params.presentationDuration,
          params.presentationSig
        )
      )
    }
  }
  return result
}
